/* eslint-disable unicorn/prefer-dom-node-dataset -- Rule is stupid*/
import { BubbleHelpFilled } from '@ori-ui/icons';
import { SHADOW_LEVEL1, styled } from '@ori-ui/mui';
import d from 'debug';
import type { FC } from 'react';
import { useState, useEffect } from 'react';

// eslint-disable-next-line import/order -- Rule has clashes
import { useConfiguration } from '../../contexts';

import './style.css';

import type {
  AppConfiguration,
  EngageInviteSettings,
  WebChatServiceStartedEvent,
} from '../../types';
import {
  getWebChatForms,
  getGenesysSettings,
  getIninChat,
  fixAndroidKeyboard,
  ininChatEvents,
} from '../../utils';

const Label = styled('div')({
  display: 'block',
  fontWeight: 700,
  opacity: 0,
  paddingRight: '20px',
  transition: 'opacity 0.3s ease-in-out',
});

const Button = styled('div')`
  -webkit-box-sizing: border-box;
  -webkit-transition: -webkit-box-shadow .15s, -webkit-transform .15s;
  align-items: center;
  background: ${({ theme }) => theme.palette.secondary.main};
  border-radius: 999px;
  border: 2px solid ${({ theme }) => theme.palette.secondary.main};
  bottom: 10px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.palette.secondary.contrastText};
  cursor: pointer;
  direction: rtl;
  display: flex;
  flex-direction: row;
  height: 45px;
  line-height: 43px;
  max-width: 45px;
  overflow: hidden;
  padding-left: ${({ theme }) => theme.spacing(4)};
  padding-right: 6px;
  padding-top: 1px;
  position: fixed;
  right: 10px;
  text-align: center;
  text-overflow: ellipsis;
  text-transform: uppercase;
  transition: -webkit-box-shadow .15s, -webkit-transform .15s;
  transition: box-shadow .15s, transform .15s, -webkit-box-shadow .15s, -webkit-transform .15s
  transition: box-shadow .15s, transform .15s;
  transition: max-width 0.3s ease-in-out 0.2s;
  vertical-align: middle;
  white-space: nowrap;
  width: auto;
  z-index: 100;

  @media screen and (min-width: 1025px) {
    right: 45px;
  }

  &:focus,
  &:hover {
    -ms-transform: scale(1.01);
    -webkit-box-shadow: ${SHADOW_LEVEL1};
    -webkit-transform: scale(1.01);
    box-shadow: ${SHADOW_LEVEL1};
    max-width: 250px;
    transform: scale(1.01);
    transition-delay: 0s;

    .genesys-widget-label {
      opacity: 1;
      transition-delay: 0.2s;
    }
  }
`;

const Icon = styled(BubbleHelpFilled)({
  display: 'flex',
  height: '30px',
  width: '30px',
});

const debug = d('genesys-widget:chat-button');

const publishDataLayerEvent = (event: string) => {
  debug(`publish data layer ${event}`);

  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({ event });
};

const SESSION_STORE_CONVERSATION_ID_KEY = 'ininChatId';

const handleWebChatServiceReady = () => {
  debug('webchat service ready');

  window.ori?.ininChat?.triggerEvent(ininChatEvents.READY);
};

const iframeStyle = {
  width: 0,
  height: 0,
};

export const ChatButton: FC<AppConfiguration> = (props) => {
  const [showButton, setShowButton] = useState<boolean | null>(null);
  const [isMinimized, setIsMinimized] = useState<boolean>(false);
  const [sessionStarted, setSessionStarted] = useState<boolean>(false);
  const [chatWindowOpened, setChatWindowOpened] = useState(false);
  const htmlContainer: HTMLElement | null = document.querySelector('.k-mobile');
  const { buttonLabel, customerId } = props;
  const { maxMessageLength } = useConfiguration();

  const handleOpenChat = () => {
    if (chatWindowOpened) {
      debug('reopen chat');

      window.ori?.genesysPlugin.command('WebChat.show');
      window.ori?.genesysPlugin.command('WebChat.minimize');

      return;
    }

    debug('open chat for the first time');

    window.ori?.genesysPlugin.command('WebChat.open', getWebChatForms(props));
  };

  const handleWebChatOpened = () => {
    debug('webchat opened');

    setChatWindowOpened(true);
    setShowButton(false);

    if (htmlContainer && htmlContainer.style.overflow !== 'hidden') {
      htmlContainer.style.overflow = 'hidden';
    }

    // attach event to change transcript height if input elemet is focused
    if (/android/i.test(navigator.userAgent)) {
      fixAndroidKeyboard();
    }
  };

  const handleWebChatMinimized = () => {
    debug('webchat minimized');

    if (htmlContainer && htmlContainer.style.overflow === 'hidden') {
      htmlContainer.style.overflow = 'inherit';
    }

    window.ori?.genesysPlugin.command('WebChat.hide');
    setShowButton(true);
    setIsMinimized(true);
  };

  const handleWebChatClosed = () => {
    debug('webchat closed');

    // window.ori?.genesysPlugin.command('WebChat.minimize');

    setChatWindowOpened(false);
    setShowButton(true);

    setIsMinimized(false);

    if (!sessionStarted) {
      window.ori?.ininChat?.handleClose();
    }
  };

  const handleWebChatServiceStarted = (event: WebChatServiceStartedEvent) => {
    debug('webchat service started', event);

    // Setting this for the bot chat to be able to switch to genesys widget automatically
    sessionStorage.setItem(SESSION_STORE_CONVERSATION_ID_KEY, event.data.data.conversationId);
    window.ori?.ininChat?.triggerEvent(ininChatEvents.READY);

    setSessionStarted(true);
  };

  const handleWebChatServiceClientConnected = () => {
    debug('webchat service client connected');
  };

  const handleWebChatServiceEnded = () => {
    debug('webchat service ended');

    if (htmlContainer && htmlContainer.style.overflow === 'hidden') {
      htmlContainer.style.overflow = 'inherit';
    }

    setSessionStarted(false);
    setShowButton(true);

    // Remove conversation id so the botchat will activate
    sessionStorage.removeItem(SESSION_STORE_CONVERSATION_ID_KEY);
  };

  // Configure localization and optionally bypass form for logged in users
  const handleEngageInvite = (settings: EngageInviteSettings) => {
    debug('engage invite', settings);

    try {
      const formsConfig = getWebChatForms(props, false);

      settings.options = { ...settings.options, ...formsConfig };

      return settings;
    } catch (error) {
      return settings;
    }
  };

  const handleInputChange = () => {
    const textArea = document.getElementById('cx_input') as HTMLTextAreaElement | null | undefined;
    const svg = document.querySelector('.cx-send.cx-icon') as HTMLElement | null | undefined;
    if (textArea && svg) {
      const { value } = textArea;
      textArea.setAttribute('data-messagelength', `${value.length}/${maxMessageLength}`);
      svg.setAttribute('data-messagelength', `${(maxMessageLength ?? 140) - value.length}`);
    }
  };

  useEffect(
    () => {
      // Check if session exists
      const sessionId = sessionStorage.getItem(SESSION_STORE_CONVERSATION_ID_KEY);

      if (sessionId) {
        setSessionStarted(true);
      }
    },
    [
      /* only check on mount  */
    ],
  );

  // add script from genesys to inject widget to page
  useEffect(() => {
    if (/android/i.test(navigator.userAgent)) {
      document.body.classList.add('device_android');
    }

    if (window.ori) {
      window.ori.ininChat = getIninChat(handleOpenChat, setShowButton);
    }

    const { scriptPath, pluginsPath, dedicatedPage } = props;
    const script = document.createElement('script');

    script.src = scriptPath;
    script.defer = true;

    script.addEventListener('load', () => {
      if (!window.ori) {
        window.ori = { genesysPlugin: {} };
      }

      const { ori } = window;

      // Configure widgets BUS
      // docs: https://all.docs.genesys.com/WID/Current/SDK/GWCBusAPIOverview
      window.CXBus.configure({ debug: false, pluginsPath });
      window.CXBus.loadPlugin('widgets-core');
      window._genesys = getGenesysSettings(props);

      // Register custom plugin to handle webchat/engage events
      ori.genesysPlugin = window.CXBus.registerPlugin('OnlineGenesysPlugin');

      // Skip chat form for logged in users for PE invitations
      // docs: https://docs.genesys.com/Documentation/GWC/latest/WidgetsAPI/EngagementCommands
      if (customerId > 0) {
        ori.genesysPlugin.before('Engage.invite', handleEngageInvite);
      }

      // Register WebChatService event handlers
      // docs: https://docs.genesys.com/Documentation/GWC/latest/WidgetsAPI/WebChatServiceEvents
      // WebChatService is initialized and ready to accept commands.
      ori.genesysPlugin.subscribe('WebChatService.ready', handleWebChatServiceReady);

      // Chat session has successfully started.
      ori.genesysPlugin.subscribe('WebChatService.started', handleWebChatServiceStarted);

      // Indicates the user has been connected to the chat session.
      ori.genesysPlugin.subscribe(
        'WebChatService.clientConnected',
        handleWebChatServiceClientConnected,
      );

      // Chat session has successfully ended.
      ori.genesysPlugin.subscribe('WebChatService.ended', handleWebChatServiceEnded);

      // Register WebChatService event handlers
      // docs: https://docs.genesys.com/Documentation/GWC/latest/WidgetsAPI/WebChatEvents
      // The WebChat widget has appeared on screen.
      ori.genesysPlugin.subscribe('WebChat.opened', handleWebChatOpened);

      // The WebChat widget has been removed from the screen.
      ori.genesysPlugin.subscribe('WebChat.closed', handleWebChatClosed);

      // The WebChat widget has been changed to a minimized state.
      ori.genesysPlugin.subscribe('WebChat.minimized', handleWebChatMinimized);

      // When the webchat service is loaded we publish an event to the dataLayer
      // this should trigger initialization of the Predictive engagement from the GTM
      publishDataLayerEvent('genesysWidgetStart');

      // autostart chat
      if (dedicatedPage) {
        setTimeout(handleOpenChat, 2000);
      }
    });

    document.addEventListener('keyup', handleInputChange);

    document.body.append(script);

    return () => script.remove();
  }, []);

  return showButton || showButton == null ? (
    <>
      <iframe
        id="ui-chatus-iframe"
        style={iframeStyle}
        title="Chat us iframe"
      />
      <Button
        id="ui-inin-chat-open"
        className={`bottom-icon${sessionStarted ? ' secondary' : ''} ${
          isMinimized ? 'is--minimized' : ''
        }`}
        // eslint-disable-next-line react/jsx-no-bind -- rule has clashes
        onClick={handleOpenChat}
      >
        <Icon />
        <Label className="genesys-widget-label">{buttonLabel}</Label>
      </Button>
    </>
  ) : null;
};
